'use client'

import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type AcademicStatus = 'studying' | 'graduated'

type GradingSystem =
  | 'percentage'
  | 'cgpa-10'
  | 'gpa-4'
  | 'grade'
  | 'other'

interface AcademicQualificationProps {
  universityName?: string
  degreeName?: string
  academicStatusName?: string
  expectedGraduationName?: string
  gradingSystemName?: string
  finalResultName?: string

  defaultUniversity?: string
  defaultDegree?: string
  defaultAcademicStatus?: AcademicStatus
  defaultExpectedGraduation?: string
  defaultGradingSystem?: GradingSystem
  defaultFinalResult?: string

  required?: boolean
  disabled?: boolean
}

interface MonthOption {
  value: string
  label: string
}

const MONTHS: MonthOption[] = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
]

const GRADING_SYSTEMS: Array<{
  value: GradingSystem
  label: string
}> = [
  {
    value: 'percentage',
    label: 'Percentage',
  },
  {
    value: 'cgpa-10',
    label: 'CGPA / 10',
  },
  {
    value: 'gpa-4',
    label: 'GPA / 4',
  },
  {
    value: 'grade',
    label: 'Grade',
  },
  {
    value: 'other',
    label: 'Other',
  },
]

function parseExpectedGraduation(value?: string): {
  month: string
  year: string
} {
  if (!value) {
    return {
      month: '',
      year: '',
    }
  }

  const match = /^(\d{4})-(\d{2})$/.exec(value)

  if (!match) {
    return {
      month: '',
      year: '',
    }
  }

  return {
    year: match[1],
    month: match[2],
  }
}

function buildExpectedGraduation(
  year: string,
  month: string,
): string {
  if (!year || !month) return ''

  return `${year}-${month}`
}

function getYearOptions(): number[] {
  const currentYear = new Date().getFullYear()

  // Allows applicants who are graduating this year,
  // while giving enough room for longer programmes.
  return Array.from(
    { length: 11 },
    (_, index) => currentYear + index,
  )
}

function resultPlaceholder(
  gradingSystem: GradingSystem | '',
): string {
  switch (gradingSystem) {
    case 'percentage':
      return 'e.g., 82.5'
    case 'cgpa-10':
      return 'e.g., 8.7'
    case 'gpa-4':
      return 'e.g., 3.6'
    case 'grade':
      return 'e.g., A'
    case 'other':
      return 'Enter your final result'
    default:
      return 'Enter your final result'
  }
}

function resultHelperText(
  gradingSystem: GradingSystem | '',
): string {
  switch (gradingSystem) {
    case 'percentage':
      return 'Enter your final percentage out of 100.'
    case 'cgpa-10':
      return 'Enter your final CGPA out of 10.'
    case 'gpa-4':
      return 'Enter your final GPA out of 4.'
    case 'grade':
      return 'Enter the final grade awarded by your institution.'
    case 'other':
      return 'Enter your result in the format used by your institution.'
    default:
      return 'Select your grading system first.'
  }
}

const AcademicQualification: React.FC<
  AcademicQualificationProps
> = ({
  universityName = 'university',
  degreeName = 'degree',
  academicStatusName = 'academicStatus',
  expectedGraduationName = 'expectedGraduation',
  gradingSystemName = 'gradingSystem',
  finalResultName = 'finalResult',

  defaultUniversity = '',
  defaultDegree = '',
  defaultAcademicStatus,
  defaultExpectedGraduation,
  defaultGradingSystem,
  defaultFinalResult = '',

  required = true,
  disabled = false,
}) => {
  const initialGraduation = useMemo(
    () => parseExpectedGraduation(defaultExpectedGraduation),
    [defaultExpectedGraduation],
  )

  const [academicStatus, setAcademicStatus] =
    useState<AcademicStatus | ''>(
      defaultAcademicStatus ?? '',
    )

  const [graduationMonth, setGraduationMonth] =
    useState(initialGraduation.month)

  const [graduationYear, setGraduationYear] =
    useState(initialGraduation.year)

  const [gradingSystem, setGradingSystem] =
    useState<GradingSystem | ''>(
      defaultGradingSystem ?? '',
    )

  const yearOptions = useMemo(
    () => getYearOptions(),
    [],
  )

  const expectedGraduation = useMemo(
    () =>
      buildExpectedGraduation(
        graduationYear,
        graduationMonth,
      ),
    [graduationMonth, graduationYear],
  )

  const studying = academicStatus === 'studying'
  const graduated = academicStatus === 'graduated'

  const expectedGraduationRef = useRef<HTMLInputElement>(null)
  const gradingSystemRef = useRef<HTMLInputElement>(null)

  // React updating a hidden input's value does not emit a native bubbling input
  // event, so dispatch one whenever each canonical value changes. The refs are
  // only attached while their branch is rendered, so these safely no-op
  // otherwise. This lets FormClient's delegated validation re-run for the field.
  useEffect(() => {
    expectedGraduationRef.current?.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [expectedGraduation])

  useEffect(() => {
    gradingSystemRef.current?.dispatchEvent(
      new Event('input', {
        bubbles: true,
      }),
    )
  }, [gradingSystem])

  return (
    <div className="space-y-4 sm:space-y-5">
      {/* ─────────────────────────────────────────────
          University + Degree
          ───────────────────────────────────────────── */}

      <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2 sm:gap-4">
        <div className="grid gap-1.5 sm:gap-2">
          <Label
            htmlFor={universityName}
            className="text-sm"
          >
            University / Institution
            {required && (
              <span
                className="text-red-500"
                aria-hidden="true"
              >
                {' '}
                *
              </span>
            )}
          </Label>

          <Input
            id={universityName}
            name={universityName}
            defaultValue={defaultUniversity}
            placeholder="e.g., Christ University"
            required={required}
            disabled={disabled}
          />
        </div>

        <div className="grid gap-1.5 sm:gap-2">
          <Label
            htmlFor={degreeName}
            className="text-sm"
          >
            Degree / Programme
            {required && (
              <span
                className="text-red-500"
                aria-hidden="true"
              >
                {' '}
                *
              </span>
            )}
          </Label>

          <Input
            id={degreeName}
            name={degreeName}
            defaultValue={defaultDegree}
            placeholder="e.g., M.Sc. Clinical Psychology"
            required={required}
            disabled={disabled}
          />
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          Academic status
          ───────────────────────────────────────────── */}

      <div className="grid gap-1.5 sm:gap-2">
        <Label className="text-sm">
          Are you currently studying?
          {required && (
            <span
              className="text-red-500"
              aria-hidden="true"
            >
              {' '}
              *
            </span>
          )}
        </Label>

        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <label className="inline-flex min-h-9 cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name={academicStatusName}
              value="studying"
              checked={studying}
              onChange={() => {
                setAcademicStatus('studying')

                // Clear the inactive graduated branch.
                setGradingSystem('')
              }}
              required={required}
              disabled={disabled}
              className="h-4 w-4 accent-primary"
            />

            <span>Yes</span>
          </label>

          <label className="inline-flex min-h-9 cursor-pointer items-center gap-2 text-sm">
            <input
              type="radio"
              name={academicStatusName}
              value="graduated"
              checked={graduated}
              onChange={() => {
                setAcademicStatus('graduated')

                // Clear the inactive studying branch.
                setGraduationMonth('')
                setGraduationYear('')
              }}
              required={required}
              disabled={disabled}
              className="h-4 w-4 accent-primary"
            />

            <span>No, I have completed my degree</span>
          </label>
        </div>
      </div>

      {/* ─────────────────────────────────────────────
          Currently studying
          ───────────────────────────────────────────── */}

      {studying && (
        <div className="rounded-lg border bg-muted/20 p-3 sm:p-4">
          <div className="grid gap-1.5 sm:gap-2">
            <Label className="text-sm">
              Expected Graduation / Pass-out
              <span
                className="text-red-500"
                aria-hidden="true"
              >
                {' '}
                *
              </span>
            </Label>

            {/*
             * Canonical value submitted to the server:
             *
             * expectedGraduation = 2027-05
             */}
            <input
              ref={expectedGraduationRef}
              type="hidden"
              name={expectedGraduationName}
              value={expectedGraduation}
              readOnly
              disabled={!studying || disabled}
            />

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Select
                value={graduationMonth}
                onValueChange={setGraduationMonth}
                disabled={disabled}
              >
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>

                <SelectContent
                  position="popper"
                  side="bottom"
                  align="start"
                  className="max-h-[min(300px,40vh)] overflow-y-auto"
                >
                  {MONTHS.map((month) => (
                    <SelectItem
                      key={month.value}
                      value={month.value}
                    >
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={graduationYear}
                onValueChange={setGraduationYear}
                disabled={disabled}
              >
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>

                <SelectContent
                  position="popper"
                  side="bottom"
                  align="start"
                  className="max-h-[min(300px,40vh)] overflow-y-auto"
                >
                  {yearOptions.map((year) => (
                    <SelectItem
                      key={year}
                      value={String(year)}
                    >
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <p className="text-[11px] text-muted-foreground sm:text-xs">
              Select the month and year you expect to
              complete your current programme.
            </p>
          </div>
        </div>
      )}

      {/* ─────────────────────────────────────────────
          Already graduated
          ───────────────────────────────────────────── */}

      {graduated && (
        <div className="rounded-lg border bg-muted/20 p-3 sm:p-4">
          <div className="grid items-start gap-4 sm:grid-cols-2">
            <div className="grid gap-1.5 sm:gap-2">
              <Label className="text-sm">
                Grading System
                <span
                  className="text-red-500"
                  aria-hidden="true"
                >
                  {' '}
                  *
                </span>
              </Label>

              <Select
                value={gradingSystem}
                onValueChange={(value) =>
                  setGradingSystem(
                    value as GradingSystem,
                  )
                }
                disabled={disabled}
              >
                <SelectTrigger className="w-full text-sm">
                  <SelectValue placeholder="Select grading system…" />
                </SelectTrigger>

                <SelectContent
                  position="popper"
                  side="bottom"
                  align="start"
                >
                  {GRADING_SYSTEMS.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/*
               * Radix's visible select does not need a name.
               * We submit one explicit canonical hidden input.
               */}
              <input
                ref={gradingSystemRef}
                type="hidden"
                name={gradingSystemName}
                value={gradingSystem}
                readOnly
                disabled={!graduated || disabled}
              />

              <p
                data-field-hint={gradingSystemName}
                className="text-[11px] text-muted-foreground sm:text-xs"
              >
                Select the grading scale used by your institution.
              </p>
            </div>

            <div className="grid gap-1.5 sm:gap-2">
              <Label
                htmlFor={finalResultName}
                className="text-sm"
              >
                Final Result
                <span
                  className="text-red-500"
                  aria-hidden="true"
                >
                  {' '}
                  *
                </span>
              </Label>

              <Input
                id={finalResultName}
                name={finalResultName}
                defaultValue={defaultFinalResult}
                placeholder={resultPlaceholder(
                  gradingSystem,
                )}
                inputMode={
                  gradingSystem === 'percentage' ||
                  gradingSystem === 'cgpa-10' ||
                  gradingSystem === 'gpa-4'
                    ? 'decimal'
                    : 'text'
                }
                required={graduated && gradingSystem !== ''}
                disabled={!graduated || disabled || gradingSystem === ''}
              />

              <p
                data-field-hint={finalResultName}
                className="text-[11px] text-muted-foreground sm:text-xs"
              >
                {resultHelperText(gradingSystem)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AcademicQualification
